import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { toggleReset } from "../reduxSlices/resetToggleSlice";
import {
  setDictionaryActive,
  setPageStructureActive,
  setVoiceNavigationActive,
} from "../reduxSlices/booleanSlice";
import { applyBackgroundColor, applyTextColor } from "../host/actions";
import {
  applyLetterSpacing,
  applyLineSpacing,
  applyWordSpacing,
} from "../host/actions";
import data from "../data/data";

function runAction(action: any) {
  if (typeof action === "function") {
    action();
  }
}

function applyProfileUpdate(
  setProfile: any,
  nextProfiles: any[],
  dispatch?: any,
) {
  if (typeof setProfile !== "function") return;

  const maybeAction = setProfile(nextProfiles);

  if (
    dispatch &&
    maybeAction &&
    typeof maybeAction === "object" &&
    "type" in maybeAction
  ) {
    dispatch(maybeAction);
  }
}

function updateProfileItemByTitle(
  profiles: any[],
  title: string,
  options?: {
    dispatch?: any;
  },
) {
  const nextProfiles = Array.isArray(profiles) ? [...profiles] : [];
  const item = nextProfiles.find((entry) => entry?.title === title);

  if (!item) {
    return nextProfiles;
  }

  if (item.totalTapCount > 0) {
    if (item.currentCount - 1 === item.totalTapCount) {
      item.isSelected = false;
      item.currentCount = 0;
      runAction(item.unSelectedAction);
    } else {
      item.isSelected = true;
      runAction(item.items?.[item.currentCount]?.subItemAction);
      item.currentCount += 1;
    }
  } else {
    item.isSelected = !item.isSelected;

    if (item.isSelected) {
      if (title === "Dictionary") {
        options?.dispatch?.(setDictionaryActive(true));
      } else if (title === "Voice Navigation") {
        options?.dispatch?.(setVoiceNavigationActive(true));
      } else if (title === "Page Structure") {
        options?.dispatch?.(setPageStructureActive(true));
      } else if (title === "Tooltips") {
        setTooltipStatus(true);
      }

      runAction(item.action);
    } else {
      if (title === "Dictionary") {
        options?.dispatch?.(setDictionaryActive(false));
      } else if (title === "Voice Navigation") {
        options?.dispatch?.(setVoiceNavigationActive(false));
      } else if (title === "Page Structure") {
        options?.dispatch?.(setPageStructureActive(false));
      } else if (title === "Tooltips") {
        removeTooltipStatus();
      }

      runAction(item.unSelectedAction);
    }
  }

  return nextProfiles;
}

function rehydrateProfileList(storedProfiles: any[]) {
  const baseProfiles = data.profile.map((profile) => ({
    ...profile,
    items: Array.isArray(profile.items)
      ? profile.items.map((item) => ({ ...item }))
      : [],
  }));

  if (!Array.isArray(storedProfiles)) {
    return baseProfiles;
  }

  const storedByTitle = new Map(
    storedProfiles
      .filter((item) => item?.title)
      .map((item) => [item.title, item]),
  );

  return baseProfiles.map((baseProfile) => {
    const storedProfile = storedByTitle.get(baseProfile.title);

    if (!storedProfile) {
      return baseProfile;
    }

    return {
      ...baseProfile,
      isSelected: !!storedProfile.isSelected,
      currentCount: Number.isFinite(storedProfile.currentCount)
        ? storedProfile.currentCount
        : 0,
      parentSelected: !!storedProfile.parentSelected,
      isVisible:
        typeof storedProfile.isVisible === "boolean"
          ? storedProfile.isVisible
          : baseProfile.isVisible,
      isMobileActive:
        typeof storedProfile.isMobileActive === "boolean"
          ? storedProfile.isMobileActive
          : baseProfile.isMobileActive,
    };
  });
}

export function setProfileList(item: any) {
  localStorage.setItem("profileList", JSON.stringify(item));
}

export function setSelectedItemIndex(index: any) {
  localStorage.setItem("selected-item-index", index);
}

export function getSelectedItem() {
  const data = localStorage.getItem("selected-item");
  if (data) {
    return JSON.parse(data);
  } else {
    return null;
  }
}

export function getSelectedItemIndex() {
  const data = localStorage.getItem("selected-item-index");
  if (data) {
    return JSON.parse(data);
  } else {
    return null;
  }
}

export function getProfileList() {
  const stored = localStorage.getItem("profileList") ?? "";

  if (!stored) {
    return rehydrateProfileList([]);
  }

  try {
    return rehydrateProfileList(JSON.parse(stored));
  } catch (error) {
    console.error("Failed to parse profileList from localStorage", error);
    return rehydrateProfileList([]);
  }
}

export function getKeyboardStatus() {
  const data = localStorage.getItem("keyboardStatus") ?? "";
  if (data) {
    return JSON.parse(data);
  } else {
    return null;
  }
}

export function setRangeSliderValues(item: any) {
  localStorage.setItem("rangeSliderValues", JSON.stringify(item));
}

export function getRangeSliderValues() {
  const data = localStorage.getItem("rangeSliderValues") ?? "";
  if (data) {
    return data;
  } else {
    return null;
  }
}

export function setColorSliderValues(item: any) {
  localStorage.setItem("colorSliderValues", JSON.stringify(item));
}

export function getColorSliderValues() {
  const data = localStorage.getItem("colorSliderValues") ?? "";
  if (data) {
    return data;
  } else {
    return null;
  }
}

export function setWidgetPosition(pos: any) {
  localStorage.setItem("widgetPosition", JSON.stringify(pos));
}

export function getWidgetPosition() {
  const data = localStorage.getItem("widgetPosition") ?? "";
  if (data) {
    return JSON.parse(data);
  } else {
    return null;
  }
}

export function setBlueLightFilter(item: any) {
  localStorage.setItem("blueLightFilter", JSON.stringify(item));
}

export function getBlueLightFilter() {
  const data = localStorage.getItem("blueLightFilter") ?? "";
  if (data) {
    return JSON.parse(data);
  } else {
    return null;
  }
}

export function setWidgetSize(size: any) {
  localStorage.setItem("widgetSize", JSON.stringify(size));
}

export function getwidgetSize() {
  const data = localStorage.getItem("widgetSize") ?? "";
  if (data) {
    return JSON.parse(data);
  } else {
    return null;
  }
}

export function hideWidgetStatus(status: any) {
  localStorage.setItem("hideWidgetStatus", JSON.stringify(status));
}

export function getHideWidgetStatus() {
  const data = localStorage.getItem("hideWidgetStatus") ?? "";
  if (data) {
    return JSON.parse(data);
  } else {
    return null;
  }
}

export function clearLocalStorageExceptHideWidgetStatus() {
  const preservedHideWidgetStatus = localStorage.getItem("hideWidgetStatus");
  const preservedLanguage = localStorage.getItem("languageSelected");

  localStorage.clear();

  if (preservedHideWidgetStatus !== null) {
    localStorage.setItem("hideWidgetStatus", preservedHideWidgetStatus);
  }

  if (preservedLanguage !== null) {
    localStorage.setItem("languageSelected", preservedLanguage);
  }
}

export function setLanguageOfUser(language: any) {
  localStorage.setItem("languageSelected", JSON.stringify(language));
}

export function getLanguageOfUser() {
  const data = localStorage.getItem("languageSelected") ?? "";
  if (data) {
    return JSON.parse(data);
  } else {
    return null;
  }
}

export function setKeyboardStatus(status: any) {
  localStorage.setItem("keyboardStatus", JSON.stringify(status));
}

export function setTooltipStatus(status: any) {
  localStorage.setItem("tooltipStatus", JSON.stringify(status));
}

export function getTooltipStatus() {
  const data = localStorage.getItem("tooltipStatus") ?? "";
  if (data) {
    return JSON.parse(data);
  } else {
    return null;
  }
}

export function removeTooltipStatus() {
  localStorage.removeItem("tooltipStatus");
}

export function percentageToPointConversion(percentage: any) {
  return percentage / 100;
}

function lineHeightFromPercent(pct: number) {
  const p = Math.max(0, Math.min(200, pct));

  const MIN = 1.0;
  const DEFAULT = 1.45;
  const MAX = 2.2;

  if (p === 100) return DEFAULT;

  if (p < 100) {
    return MIN + (DEFAULT - MIN) * (p / 100);
  }

  return DEFAULT + (MAX - DEFAULT) * ((p - 100) / 100);
}

export function isMobile() {
  return window.innerWidth <= 499;
}

export function useIsMobile() {
  const [mobile, setMobile] = useState(isMobile());

  useEffect(() => {
    function handleResize() {
      setMobile(window.innerWidth <= 499);
    }

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return mobile;
}

export const useKeyBoardShortcut = (
  profile: any[],
  setProfile: any,
  setProfileListCallback: any,
) => {
  const dispatch = useDispatch();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const keyboardStatus = getKeyboardStatus();
      if (!keyboardStatus) return;

      const key = String(e.key || "").toLowerCase();
      const tooltipStatus = !!getTooltipStatus();

      if (key === "r") {
        dispatch(toggleReset(true));
        return;
      }

      const keyMap: Record<string, string> = {
        s: "Screen Reader",
        p: "Pause Animation",
        l: "Tooltips",
        u: "Big Cursor",
        d: "Dyslexia Fonts",
        v: "Voice Navigation",
        i: "Hide Images",
        k: "Dictionary",
        g: "Page Structure",
        y: "Low Saturation",
        h: "Highlight Links",
        m: "Align Text",
        c: "High Contrast",
      };

      const targetTitle = keyMap[key];
      if (!targetTitle) return;

      e.preventDefault();

      if (targetTitle === "Tooltips") {
        if (tooltipStatus) {
          removeTooltipStatus();
        } else {
          setTooltipStatus(true);
        }
      }

      const nextProfiles = updateProfileItemByTitle(profile, targetTitle, {
        dispatch,
      });

      applyProfileUpdate(setProfile, nextProfiles, dispatch);
      setProfileListCallback(nextProfiles);
    };

    document.addEventListener("keydown", handleKeyDown, true);

    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [profile, setProfile, setProfileListCallback, dispatch]);
};

export const usePageStructureSync = (
  profile: any[],
  setProfile: any,
  setProfileListCallback: any,
) => {
  const dispatch = useDispatch();

  useEffect(() => {
    const setStateFromOpen = (isOpen: boolean) => {
      document.body.classList.toggle("parent-modal-open", isOpen);

      const nextProfiles = Array.isArray(profile) ? [...profile] : [];

      for (let i = 0; i < nextProfiles.length; i++) {
        if (nextProfiles[i].title === "Page Structure") {
          nextProfiles[i].isSelected = isOpen;
          if (nextProfiles[i].totalTapCount > 0) {
            nextProfiles[i].currentCount = isOpen ? 1 : 0;
          }
        }
      }

      dispatch(setPageStructureActive(isOpen));
      applyProfileUpdate(setProfile, nextProfiles, dispatch);
      setProfileListCallback(nextProfiles);
    };

    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === "PAGE_STRUCTURE_STATUS") {
        setStateFromOpen(!!event.data.isOpen);
      }
    };

    const handleCustomEvent = (event: Event) => {
      const custom = event as CustomEvent;
      setStateFromOpen(!!custom.detail?.isOpen);
    };

    window.addEventListener("message", handleMessage);
    window.addEventListener(
      "incluway:page-structure-status",
      handleCustomEvent as EventListener,
    );

    return () => {
      window.removeEventListener("message", handleMessage);
      window.removeEventListener(
        "incluway:page-structure-status",
        handleCustomEvent as EventListener,
      );
    };
  }, [profile, setProfile, setProfileListCallback, dispatch]);
};

export const useVoiceNavSync = (
  profile: any[],
  setProfile: any,
  setProfileListCallback: any,
) => {
  const dispatch = useDispatch();

  useEffect(() => {
    const setStateFromOpen = (isOpen: boolean) => {
      const nextProfiles = Array.isArray(profile) ? [...profile] : [];

      for (let i = 0; i < nextProfiles.length; i++) {
        if (nextProfiles[i].title === "Voice Navigation") {
          nextProfiles[i].isSelected = isOpen;
          if (!isOpen) {
            nextProfiles[i].currentCount = 0;
          }
        }
      }

      dispatch(setVoiceNavigationActive(isOpen));
      applyProfileUpdate(setProfile, nextProfiles, dispatch);
      setProfileListCallback(nextProfiles);
    };

    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === "VOICE_NAV_STATUS") {
        setStateFromOpen(!!event.data.isOpen);
      }
    };

    const handleCustomEvent = (event: Event) => {
      const custom = event as CustomEvent;
      setStateFromOpen(!!custom.detail?.isOpen);
    };

    window.addEventListener("message", handleMessage);
    window.addEventListener(
      "incluway:voice-nav-status",
      handleCustomEvent as EventListener,
    );

    return () => {
      window.removeEventListener("message", handleMessage);
      window.removeEventListener(
        "incluway:voice-nav-status",
        handleCustomEvent as EventListener,
      );
    };
  }, [profile, setProfile, setProfileListCallback, dispatch]);
};

export const applyTextSettingsFromStorage = () => {
  const storedFontSizeData = localStorage.getItem("rangeSliderValues") ?? "";
  if (!storedFontSizeData) return;

  const storedFontSizeDataObject = JSON.parse(storedFontSizeData);

  if (storedFontSizeDataObject?.letterSpacing !== undefined) {
    const pointValue = percentageToPointConversion(
      storedFontSizeDataObject.letterSpacing,
    );

    applyLetterSpacing(pointValue);
  }

  if (storedFontSizeDataObject?.wordSpacing !== undefined) {
    const pointValue = percentageToPointConversion(
      storedFontSizeDataObject.wordSpacing,
    );

    applyWordSpacing(pointValue);
  }

  if (storedFontSizeDataObject?.lineSpacing !== undefined) {
    const pointValue = lineHeightFromPercent(
      Number(storedFontSizeDataObject.lineSpacing),
    );

    applyLineSpacing(pointValue);
  }
};

export const applyStoredSettingsOnStartup = () => {
  applyTextSettingsFromStorage();

  const storedColorData = localStorage.getItem("colorSliderValues") ?? "";
  if (storedColorData) {
    try {
      const parsed = JSON.parse(storedColorData);

      applyBackgroundColor(parsed?.backgroundColor || "");
      applyTextColor(parsed?.textColor || "");
    } catch (error) {
      console.error(
        "Failed to parse colorSliderValues from localStorage",
        error,
      );
    }
  }

  const storedProfiles = getProfileList();

  if (Array.isArray(storedProfiles)) {
    for (let i = 0; i < storedProfiles.length; i++) {
      if (!storedProfiles[i]?.isSelected) continue;

      if (storedProfiles[i].totalTapCount > 0) {
        runAction(
          storedProfiles[i].items?.[storedProfiles[i].currentCount - 1]
            ?.subItemAction,
        );
      } else {
        runAction(storedProfiles[i].action);
      }
    }
  }
};

window.addEventListener("keydown", (event) => {
  if (event.altKey && String(event.key).toLowerCase() === "1") {
    hideWidgetStatus(false);
  }
});

export const LANGUAGES = {
  en: {
    name: "English",
    dictApiCode: "en",
  },
  fr: {
    name: "French",
    dictApiCode: "fr",
  },
  es: {
    name: "Spanish",
    dictApiCode: "es",
  },
  it: {
    name: "Italian",
    dictApiCode: "it",
  },
  ru: {
    name: "Russian",
    dictApiCode: "ru",
  },
  nl: {
    name: "Dutch",
    dictApiCode: "nl",
  },
  tr: {
    name: "Turkish",
    dictApiCode: "tr",
  },
  pl: {
    name: "Polish",
    dictApiCode: "pl",
  },
  gr: {
    name: "German",
    dictApiCode: "de",
  },
  ar: {
    name: "Arabic",
    dictApiCode: "ar",
  },
  "zh-Hans": {
    name: "Chinese (Simplified)",
    dictApiCode: "zh",
  },
};

export function simpleLanguageDetection(text: any) {
  const patterns = {
    gr: /[äöüßÄÖÜ]/,
    fr: /[éèêëàâçîïôûùüÿÉÈÊËÀÂÇÎÏÔÛÙÜŸ]/,
    es: /[áéíóúüñÁÉÍÓÚÜÑ]/,
    ru: /[а-яА-ЯёЁ]/,
    it: /[àèéìíîòóùúÀÈÉÌÍÎÒÓÙÚ]/,
    nl: /[ijàéèêëïçÇËÏÉÈÊÄÖÜ]/,
    pl: /[ąćęłńóśźżĄĆĘŁŃÓŚŹŻ]/,
    tr: /[çğıöşüÇĞİÖŞÜ]/,
    ar: /[\u0600-\u06FF]/,
    "zh-Hans": /[\u3400-\u9FFF]/,
  };

  for (const [lang, pattern] of Object.entries(patterns)) {
    if (pattern.test(text)) {
      return lang;
    }
  }

  return "en";
}
